import { useCallback, useEffect, useSyncExternalStore } from 'react';
import type { Settings } from '../game/persistence/schema';
import { selectRenderTheme, type RenderTheme } from '../game/rendering/theme';

/**
 * Resolves user settings against system preferences and applies them to the document.
 *
 * `prefers-color-scheme`, `prefers-reduced-motion` and `prefers-contrast` are honoured
 * whenever the corresponding setting is left on "system", and the resolved values drive
 * both the CSS custom properties and the canvas palette.
 */

/**
 * Subscribes to a media query through `useSyncExternalStore`, which is the right tool for
 * external state: no effect, no cascading render, and the first paint already has the
 * correct value.
 */
function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof matchMedia !== 'function') return () => undefined;
      const list = matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );

  const getSnapshot = useCallback(
    () => (typeof matchMedia === 'function' ? matchMedia(query).matches : false),
    [query],
  );

  // The server snapshot is only reached in non-browser rendering, where no media applies.
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

export interface ResolvedPreferences {
  readonly dark: boolean;
  readonly highContrast: boolean;
  readonly reducedMotion: boolean;
  readonly renderTheme: RenderTheme;
}

export function useResolvedPreferences(settings: Settings): ResolvedPreferences {
  const systemDark = useMediaQuery('(prefers-color-scheme: dark)');
  const systemReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const systemContrast = useMediaQuery('(prefers-contrast: more)');

  const dark = settings.theme === 'system' ? systemDark : settings.theme === 'dark';
  const highContrast = settings.highContrast || systemContrast;
  const reducedMotion =
    settings.motion === 'system' ? systemReducedMotion : settings.motion === 'reduced';

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = dark ? 'dark' : 'light';
    root.dataset.contrast = highContrast ? 'high' : 'normal';
    root.dataset.motion = reducedMotion ? 'reduced' : 'full';
  }, [dark, highContrast, reducedMotion]);

  return {
    dark,
    highContrast,
    reducedMotion,
    renderTheme: selectRenderTheme(dark, highContrast),
  };
}

/** True when the page is visible; used to pause the timer and audio. */
export function useDocumentVisible(): boolean {
  return useSyncExternalStore(subscribeToVisibility, isDocumentVisible, () => true);
}

function subscribeToVisibility(onChange: () => void): () => void {
  document.addEventListener('visibilitychange', onChange);
  return () => document.removeEventListener('visibilitychange', onChange);
}

function isDocumentVisible(): boolean {
  return typeof document === 'undefined' ? true : !document.hidden;
}
